/**
 * Central auth-state: session, profil, rolle og afledte flags.
 * Roller ligger i user_roles (ikke på profiles). SUPERADMIN > SALESPERSON > COMPANY_ADMIN > COMPANY_USER.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";

export type AppRole = "SUPERADMIN" | "COMPANY_ADMIN" | "COMPANY_USER" | "SALESPERSON";
export type ProfileStatus = "pending" | "active" | "inactive" | "suspended";

export interface Profile {
  id: string;
  user_id: string;
  company_id: string | null;
  name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  status: ProfileStatus;
  must_change_password: boolean;
  /** Hvornår onboarding-dialogen er set (null = vis ved næste login). */
  onboarded_at: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  loading: boolean;
  /** true når session findes men profil/rolle stadig hentes */
  profileLoading: boolean;
  isSuperAdmin: boolean;
  isSalesperson: boolean;
  isCompanyAdmin: boolean;
  mfaRequired: boolean;
  setMfaResolved: () => void;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const ROLE_PRIORITY: AppRole[] = ["SUPERADMIN", "SALESPERSON", "COMPANY_ADMIN", "COMPANY_USER"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  const loadProfileAndRole = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const [profileRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
      ]);
      if (profileRes.error) throw profileRes.error;
      if (rolesRes.error) throw rolesRes.error;
      setProfile((profileRes.data as unknown as Profile) ?? null);
      const roles = (rolesRes.data ?? []).map((r) => r.role as AppRole);
      setRole(ROLE_PRIORITY.find((r) => roles.includes(r)) ?? null);
    } catch (e) {
      logger.error("auth_profile_load_failed", { error: String(e) });
      setProfile(null);
      setRole(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const checkMfa = useCallback(async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      // Har brugeren en verificeret faktor, men er kun på aal1 → kræv MFA-verifikation
      setMfaRequired(data?.nextLevel === "aal2" && data?.currentLevel !== "aal2");
    } catch {
      setMfaRequired(false);
    }
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        // Defer Supabase-kald ud af callbacken (deadlock-guard)
        setTimeout(() => {
          void loadProfileAndRole(newSession.user.id);
          void checkMfa();
        }, 0);
      } else {
        setProfile(null);
        setRole(null);
        setMfaRequired(false);
      }
      setLoading(false);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        void loadProfileAndRole(data.session.user.id);
        void checkMfa();
      }
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, [loadProfileAndRole, checkMfa]);

  const isSuperAdmin = role === "SUPERADMIN";
  const value: AuthState = {
    user: session?.user ?? null,
    session,
    profile,
    role,
    loading,
    profileLoading,
    isSuperAdmin,
    isSalesperson: role === "SALESPERSON" || isSuperAdmin,
    isCompanyAdmin: role === "COMPANY_ADMIN" || isSuperAdmin,
    mfaRequired,
    setMfaResolved: () => setMfaRequired(false),
    refreshProfile: async () => {
      if (session?.user) await loadProfileAndRole(session.user.id);
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth skal bruges inden for AuthProvider");
  return ctx;
}
